import {
  getConsultantGradient,
  getConsultantInitials,
  getConsultantShortName,
} from '../booking/utils/consultantDisplay';
import { getConsultantMock } from '../booking/data/consultantMockData';
import { HOME_TOPIC_SECTIONS } from './homeTopicSections';
import type { HomeTopicSectionConfig } from './homeTopicSections';
import type { Service } from './components/ServiceCard';
import type { AppointmentTaker } from './types';
import { HOME_SECTION_TILE_ICONS, homeTopicIconForItem } from './homeTopicTileIcons';

export type HomeSearchAction =
  | { type: 'topic'; categoryId: string; slug: string }
  | { type: 'store'; slug: string }
  | { type: 'consultant'; slug: string }
  | { type: 'scroll'; elementId: string };

/** Matches home quick tiles: emergency = soft gray; legal/financial/etc. = red gradient. */
export type HomeSearchIconVariant = 'red' | 'soft';

export type HomeSearchVisual =
  | { type: 'icon'; iconClass: string; variant: HomeSearchIconVariant }
  | { type: 'image'; src: string; alt?: string }
  | { type: 'initials'; text: string; background: string };

function iconVariantForCategory(categoryId: string): HomeSearchIconVariant {
  return categoryId === 'emergency' ? 'soft' : 'red';
}

function iconVariantForSection(sectionId: string): HomeSearchIconVariant {
  return sectionId === 'emergency' ? 'soft' : 'red';
}

export interface HomeSearchCatalogEntry {
  id: string;
  title: string;
  subtitle?: string;
  group: string;
  searchText: string;
  visual: HomeSearchVisual;
  action: HomeSearchAction;
}

const QUICK_SECTION_SCROLL_ID: Record<string, string> = {
  emergency: 'home-section-towing',
  legal: 'home-section-legal',
  financial: 'home-section-financial',
  vehicle: 'home-section-vehicle',
  professionals: 'home-section-professionals',
};

function normalize(text: string): string {
  return text.toLowerCase().replace(/\s+/g, ' ').trim();
}

function sectionIcon(section: HomeTopicSectionConfig): string {
  if (section.eyebrowIcon) return section.eyebrowIcon;
  return HOME_SECTION_TILE_ICONS[section.id] ?? 'ti-layout-grid';
}

function entry(
  partial: Omit<HomeSearchCatalogEntry, 'searchText'> & { extra?: string }
): HomeSearchCatalogEntry {
  const { extra, ...rest } = partial;
  const searchText = normalize([rest.title, rest.subtitle, rest.group, extra].filter(Boolean).join(' '));
  return { ...rest, searchText };
}

function scrollIdForSection(sectionId: string): string {
  return QUICK_SECTION_SCROLL_ID[sectionId] ?? `home-section-${sectionId}`;
}

function catalogFromTopicSections(sections: HomeTopicSectionConfig[]): HomeSearchCatalogEntry[] {
  const out: HomeSearchCatalogEntry[] = [];
  for (const section of sections) {
    const group = section.eyebrow || section.title;
    out.push(
      entry({
        id: `section-${section.id}`,
        title: section.titleLead
          ? [section.titleLead, section.titleTail].filter(Boolean).join(' ')
          : section.title,
        subtitle: section.subtitle,
        group,
        extra: section.eyebrow,
        visual: {
          type: 'icon',
          iconClass: sectionIcon(section),
          variant: iconVariantForSection(section.id),
        },
        action: { type: 'scroll', elementId: scrollIdForSection(section.id) },
      })
    );
    for (const item of section.items) {
      out.push(
        entry({
          id: `topic-${item.categoryId}-${item.slug}`,
          title: item.title,
          subtitle: item.shortDesc,
          group,
          extra: item.meta,
          visual: {
            type: 'icon',
            iconClass: homeTopicIconForItem(item.slug, item.categoryId, item.iconKey),
            variant: iconVariantForCategory(item.categoryId),
          },
          action: { type: 'topic', categoryId: item.categoryId, slug: item.slug },
        })
      );
    }
  }
  return out;
}

export function buildHomeSearchCatalog(params: {
  topicSections?: HomeTopicSectionConfig[];
  storeServices: Service[];
  consultants: AppointmentTaker[];
}): HomeSearchCatalogEntry[] {
  const sections = params.topicSections ?? HOME_TOPIC_SECTIONS;
  const catalog: HomeSearchCatalogEntry[] = [
    ...catalogFromTopicSections(sections),
    entry({
      id: 'landmark-store',
      title: 'CWY Store',
      subtitle: 'Services built for your goals',
      group: 'On this page',
      visual: { type: 'icon', iconClass: 'ti-shopping-bag', variant: 'red' },
      action: { type: 'scroll', elementId: 'home-section-store' },
    }),
    entry({
      id: 'landmark-consultants',
      title: 'Meet our experts',
      subtitle: 'Book consultants and RCIC advisors',
      group: 'On this page',
      visual: { type: 'icon', iconClass: 'ti-users', variant: 'red' },
      action: { type: 'scroll', elementId: 'home-section-consultants' },
    }),
  ];

  for (const svc of params.storeServices) {
    catalog.push(
      entry({
        id: `store-${svc.slug}`,
        title: svc.title,
        subtitle: svc.shortDesc ?? svc.category,
        group: 'CWY Store',
        extra: svc.category,
        visual: svc.image
          ? { type: 'image', src: svc.image, alt: '' }
          : { type: 'icon', iconClass: 'ti-shopping-bag', variant: 'red' },
        action: { type: 'store', slug: svc.slug },
      })
    );
  }

  for (const taker of params.consultants) {
    const slug = taker.slug ?? taker.userId;
    const mock = getConsultantMock(slug, taker.displayName);
    const shortName = getConsultantShortName(taker.displayName);
    const initials = getConsultantInitials(shortName);
    const gradient = getConsultantGradient(slug);
    catalog.push(
      entry({
        id: `consultant-${slug}`,
        title: taker.displayName,
        subtitle: taker.officeAddress?.city ?? 'Consultant',
        group: 'Consultants',
        extra: taker.email,
        visual: mock?.photo
          ? { type: 'image', src: mock.photo, alt: shortName }
          : {
              type: 'initials',
              text: initials,
              background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
            },
        action: { type: 'consultant', slug },
      })
    );
  }

  const seen = new Set<string>();
  return catalog.filter((row) => {
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
}

export function filterHomeSearchCatalog(
  catalog: HomeSearchCatalogEntry[],
  query: string,
  limit = 10
): HomeSearchCatalogEntry[] {
  const q = normalize(query);
  if (!q) return [];

  const tokens = q.split(' ').filter(Boolean);
  const scored: { entry: HomeSearchCatalogEntry; score: number }[] = [];

  for (const row of catalog) {
    if (!tokens.every((t) => row.searchText.includes(t))) continue;
    let score = 0;
    const title = normalize(row.title);
    if (title.startsWith(q)) score += 4;
    else if (title.includes(q)) score += 2;
    if (normalize(row.group).includes(q)) score += 1;
    scored.push({ entry: row, score });
  }

  scored.sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title));
  return scored.slice(0, limit).map((s) => s.entry);
}
